import React from "react";
import styled from "styled-components";
import {
  FaApple,
  FaTrophy,
  FaStar,
  FaLinkedin,
  FaInstagram,
  FaGithub,
} from "react-icons/fa";
import officeImage from "../assets/images/me.jpeg";

/* ─── data ─── */

const achievements = [
  {
    icon: <FaApple />,
    title: "Apple WWDC Winner",
    subtitle: "Swift Student Challenge 2024",
    color: "#10b981",
  },
  {
    icon: <FaTrophy />,
    title: "100 Jóvenes Líderes",
    subtitle: "La Tercera / El Mercurio 2024",
    color: "#f59e0b",
  },
  {
    icon: <FaStar />,
    title: "Top 50 Developers",
    subtitle: "Apple — Mundial",
    color: "#7c3aed",
  },
];

const socials = [
  {
    icon: <FaLinkedin />,
    url: "https://www.linkedin.com/in/martin-seguel/",
    label: "LinkedIn",
  },
  {
    icon: <FaInstagram />,
    url: "https://www.instagram.com/martinseguel__/",
    label: "Instagram",
  },
  {
    icon: <FaGithub />,
    url: "https://github.com/martseguel",
    label: "GitHub",
  },
];

const timeline = [
  { year: "2020", text: "Ingresa a Ingeniería Civil Informática, UAI" },
  { year: "2023", text: "Funda Monaco IT Services" },
  { year: "2024", text: "Primer chileno en ganar Apple Swift Student Challenge" },
  { year: "2024", text: "Viaja a Cupertino y conoce a Tim Cook" },
  { year: "2024", text: "Reconocido como uno de los 100 Jóvenes Líderes" },
  { year: "2025", text: "Lanza Gymly — ya operando en Chile y Perú" },
];

/* ─── styled ─── */

const Section = styled.section`
  padding: 100px 0;
  background: #0f1123;
  color: white;
  overflow: hidden;

  @media (max-width: 900px) {
    padding: 60px 0;
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 900px) {
    padding: 0 20px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const SectionTag = styled.span`
  display: inline-block;
  background: rgba(0, 200, 255, 0.08);
  border: 1px solid rgba(0, 200, 255, 0.2);
  color: #00c8ff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 8px 20px;
  border-radius: 50px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 2.8em;
  color: #ffffff;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin-bottom: 16px;

  span {
    background: linear-gradient(135deg, #00c8ff, #005bea);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 900px) {
    font-size: 2em;
  }
`;

const SectionSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.45);
  font-size: 1.1em;
  max-width: 560px;
  margin: 0 auto;
  line-height: 1.7;

  @media (max-width: 900px) {
    font-size: 1em;
  }
`;

/* ── top row: photo + bio ── */

const TopRow = styled.div`
  display: flex;
  gap: 64px;
  align-items: center;
  margin-bottom: 64px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const ImageCol = styled.div`
  flex: 0 0 380px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: -12px;
    left: -12px;
    right: 12px;
    bottom: 12px;
    border: 2px solid rgba(0, 200, 255, 0.15);
    border-radius: 22px;
  }

  @media (max-width: 900px) {
    flex: none;
    width: 100%;
    max-width: 320px;
  }
`;

const Photo = styled.img`
  width: 100%;
  height: auto;
  border-radius: 18px;
  filter: grayscale(100%);
  position: relative;
  z-index: 1;
  transition: filter 0.5s ease;

  &:hover {
    filter: grayscale(0%);
  }
`;

const AppleSticker = styled.div`
  position: absolute;
  top: -18px;
  right: -18px;
  z-index: 3;
  width: 116px;
  height: 116px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffffff 0%, #e8e8e8 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.45),
    0 0 0 4px rgba(15, 17, 35, 1);
  transform: rotate(8deg);
  transition: transform 0.4s ease;

  &:hover {
    transform: rotate(0deg) scale(1.05);
  }

  svg {
    color: #0f1123;
    font-size: 32px;
    margin-bottom: 4px;
  }

  .sticker-eyebrow {
    font-size: 7px;
    font-weight: 800;
    letter-spacing: 1.4px;
    color: rgba(15, 17, 35, 0.6);
    text-transform: uppercase;
  }

  .sticker-title {
    font-size: 11px;
    font-weight: 800;
    color: #0f1123;
    letter-spacing: 0.2px;
    line-height: 1.1;
    margin-top: 1px;
  }

  .sticker-year {
    font-size: 9px;
    font-weight: 700;
    color: #00c8ff;
    letter-spacing: 1.2px;
    margin-top: 3px;
  }

  @media (max-width: 900px) {
    width: 96px;
    height: 96px;
    top: -14px;
    right: -14px;
    svg { font-size: 26px; }
    .sticker-eyebrow { font-size: 6px; }
    .sticker-title { font-size: 10px; }
    .sticker-year { font-size: 8px; }
  }
`;

const BioCol = styled.div`
  flex: 1;
`;

const Name = styled.h3`
  font-size: 1.6em;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 4px;
`;

const Role = styled.p`
  color: #00c8ff;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const RoleSub = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 24px;
  letter-spacing: 0.3px;
`;

const Bio = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 15px;
  line-height: 1.85;

  strong {
    color: #ffffff;
  }

  @media (max-width: 900px) {
    text-align: justify;
    font-size: 14px;
  }
`;

const SocialsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 28px;
`;

const SocialLink = styled.a`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 18px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 200, 255, 0.1);
    border-color: rgba(0, 200, 255, 0.3);
    color: #00c8ff;
    transform: translateY(-3px);
  }
`;

/* ── achievements row ── */

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 64px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const AchievementCard = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "accentColor",
})`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 28px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${({ accentColor }) => accentColor}30;
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  }
`;

const AchievementIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "accentColor",
})`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${({ accentColor }) => accentColor}12;
  border: 1px solid ${({ accentColor }) => accentColor}25;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${({ accentColor }) => accentColor};
  flex-shrink: 0;
`;

const AchievementText = styled.div``;

const AchievementTitle = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2px;
`;

const AchievementSub = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 500;
`;

/* ── timeline ── */

const TimelineTitle = styled.h3`
  font-size: 1.2em;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin-bottom: 36px;

  span {
    color: rgba(255, 255, 255, 0.3);
    font-weight: 500;
  }
`;

const TimelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TimelineItem = styled.div`
  padding: 20px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-left: 3px solid rgba(0, 200, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-left-color: #00c8ff;
  }
`;

const TimelineYear = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: #00c8ff;
  letter-spacing: 1px;
`;

const TimelineText = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
  margin-top: 6px;
`;

/* ─── component ─── */

export const About = () => {
  return (
    <Section id="about">
      <Inner>
        <Header>
          <SectionTag>El fundador</SectionTag>
          <SectionTitle>
            Sobre <span>Martín Seguel</span>
          </SectionTitle>
          <SectionSubtitle>
            La historia detrás de Monaco IT Services y una trayectoria
            reconocida internacionalmente.
          </SectionSubtitle>
        </Header>

        {/* ── photo + bio ── */}
        <TopRow>
          <ImageCol>
            <Photo src={officeImage} alt="Martín Seguel — Fundador de Monaco IT Services, Ingeniero Civil Informático y ganador del Apple Swift Student Challenge" />
            <AppleSticker>
              <FaApple />
              <span className="sticker-eyebrow">Winner</span>
              <span className="sticker-title">Swift Student<br />Challenge</span>
              <span className="sticker-year">WWDC '24</span>
            </AppleSticker>
          </ImageCol>
          <BioCol>
            <Name>Martín Seguel</Name>
            <Role>Fundador de Monaco IT Services</Role>
            <RoleSub>
              Ingeniero Civil Informático — Universidad Adolfo Ibáñez
            </RoleSub>
            <Bio>
              Nací y crecí en Los Ángeles, Chile. Desde pequeño tuve una gran
              pasión por la tecnología. Durante mis estudios en la UAI, me
              convertí en el <strong>primer chileno en ganar el Apple Swift
              Student Challenge</strong>, viajando a Cupertino donde conocí a
              Tim Cook.
              <br />
              <br />
              Pese a recibir ofertas en Silicon Valley, decidí volver a Chile y
              fundar <strong>Monaco IT Services</strong> — una consultora
              enfocada en crear software de alto impacto para startups y empresas
              que buscan potenciar su crecimiento. Hoy lidero proyectos con un
              equipo de diseñadores y desarrolladores, construyendo desde apps
              móviles hasta plataformas de inteligencia artificial.
            </Bio>
            <SocialsRow>
              {socials.map((s, i) => (
                <SocialLink
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.label}
                >
                  {s.icon}
                </SocialLink>
              ))}
            </SocialsRow>
          </BioCol>
        </TopRow>

        {/* ── achievements ── */}
        <AchievementsGrid>
          {achievements.map((a, i) => (
            <AchievementCard key={i} accentColor={a.color}>
              <AchievementIcon accentColor={a.color}>
                {a.icon}
              </AchievementIcon>
              <AchievementText>
                <AchievementTitle>{a.title}</AchievementTitle>
                <AchievementSub>{a.subtitle}</AchievementSub>
              </AchievementText>
            </AchievementCard>
          ))}
        </AchievementsGrid>

        {/* ── timeline ── */}
        <TimelineTitle>
          Trayectoria <span>— hitos clave</span>
        </TimelineTitle>
        <TimelineGrid>
          {timeline.map((t, i) => (
            <TimelineItem key={i}>
              <TimelineYear>{t.year}</TimelineYear>
              <TimelineText>{t.text}</TimelineText>
            </TimelineItem>
          ))}
        </TimelineGrid>
      </Inner>
    </Section>
  );
};
